import { type ReactNode, useCallback, useMemo, useState } from "react";

import {
  DATE_OVERRIDE_STORAGE_KEY,
  DateOverrideContext,
  type DateOverrideValues,
  EMPTY_DATE_OVERRIDE,
  loadDateOverrides,
} from "./dateOverrideContext";

function saveOverrides(values: DateOverrideValues): void {
  try {
    localStorage.setItem(DATE_OVERRIDE_STORAGE_KEY, JSON.stringify(values));
  } catch {
    // ignore — private browsing or storage full
  }
}

export function DateOverrideProvider({ children }: { children: ReactNode }) {
  const [values, setValuesState] = useState<DateOverrideValues>(loadDateOverrides);

  const setField = useCallback((field: keyof DateOverrideValues, value: string) => {
    setValuesState((prev) => {
      const next = { ...prev, [field]: value };
      saveOverrides(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setValuesState(EMPTY_DATE_OVERRIDE);
    saveOverrides(EMPTY_DATE_OVERRIDE);
  }, []);

  const hasOverrides = useMemo(
    () => Object.values(values).some((v) => v !== ""),
    [values],
  );

  const ctxValue = useMemo(
    () => ({ ...values, setField, clearAll, hasOverrides }),
    [values, setField, clearAll, hasOverrides],
  );

  return (
    <DateOverrideContext.Provider value={ctxValue}>
      {children}
    </DateOverrideContext.Provider>
  );
}
